/**
 * Client-side Query Token Detector
 * Mirrors backend logic for UI preview
 */

const SUGAR_ABBREVIATIONS = [
  'Glc', 'Gal', 'Man', 'All', 'Alt', 'Gul', 'Ido', 'Tal',
  'Fuc', 'Rha', 'Qui',
  'Ara', 'Xyl', 'Rib', 'Lyx',
  'GlcA', 'GalA', 'ManA', 'IdoA',
  'GlcN', 'GalN', 'ManN',
  'GlcNAc', 'GalNAc', 'ManNAc',
  'Neu', 'Fru', 'Kdo', 'Hep'
];

const SPECIES_KEYWORDS = [
  'escherichia', 'klebsiella', 'salmonella', 'shigella',
  'pseudomonas', 'staphylococcus', 'streptococcus',
  'enterococcus', 'bacillus', 'listeria', 'vibrio',
  'coli', 'pneumoniae', 'aeruginosa', 'aureus'
];

/**
 * Tokenize query (with quote support)
 */
export function tokenizeQuery(query) {
  if (!query || typeof query !== 'string') {
    return [];
  }

  const tokens = [];
  let currentToken = '';
  let inQuotes = false;
  let quoteChar = null;

  for (let i = 0; i < query.length; i++) {
    const char = query[i];

    if ((char === '"' || char === "'") && !inQuotes) {
      inQuotes = true;
      quoteChar = char;
      continue;
    } else if (char === quoteChar && inQuotes) {
      inQuotes = false;
      quoteChar = null;
      if (currentToken.length > 0) {
        tokens.push(currentToken);
        currentToken = '';
      }
      continue;
    }

    if (inQuotes) {
      currentToken += char;
    } else if (char === ' ' || char === '\t') {
      if (currentToken.length > 0) {
        tokens.push(currentToken);
        currentToken = '';
      }
    } else {
      currentToken += char;
    }
  }

  if (currentToken.length > 0) {
    tokens.push(currentToken);
  }

  return tokens;
}

/**
 * Detect module for a token
 */
export function detectModule(token) {
  if (!token || token.trim() === '') {
    return 'unknown';
  }

  const normalized = token.trim();

  // 1. SEROTYPE
  if (/^[OK]\d+[A-Z0-9]*$/i.test(normalized)) {
    return 'serotype';
  }

  // 2. NMR
  if (/^\d+[.,]?\d*$/.test(normalized)) {
    return 'nmr';
  }
  if (/^\d+[.,]?\d*\s*-\s*\d+[.,]?\d*$/.test(normalized)) {
    return 'nmr';
  }
  if (/^\d+[.,]\d+[,;]\d+/.test(normalized)) {
    return 'nmr';
  }

  // 3. SPECIES
  const lowerToken = normalized.toLowerCase();
  const matchesSpeciesKeyword = SPECIES_KEYWORDS.some(keyword =>
    lowerToken.includes(keyword)
  );
  if (matchesSpeciesKeyword) {
    return 'species';
  }
  if (/^[A-Z][a-z]{3,}/.test(normalized)) {
    return 'species';
  }

  // 4. CARBUILDER
  const hasBondNotation = /[αβ→]/.test(normalized) || /[ab]\d+-/.test(normalized);
  if (hasBondNotation) {
    return 'carbuilder';
  }
  if (normalized.includes('*')) {
    return 'carbuilder';
  }
  if (/-/.test(normalized) && normalized.length > 3) {
    const parts = normalized.split('-');
    const looksLikeSugars = parts.some(part => /^[A-Z][a-z]+/.test(part));
    if (looksLikeSugars) {
      return 'carbuilder';
    }
  }
  const containsSugar = SUGAR_ABBREVIATIONS.some(sugar =>
    normalized.toLowerCase().includes(sugar.toLowerCase())
  );
  if (containsSugar) {
    return 'carbuilder';
  }

  return 'unknown';
}

/**
 * Parse query and return token-module pairs
 */
export function parseMultiModuleQuery(query) {
  const tokens = tokenizeQuery(query);
  return tokens.map(token => ({
    token: token,
    module: detectModule(token)
  }));
}

/**
 * Check if query is multi-module
 */
export function isMultiModuleQuery(query) {
  const parsed = parseMultiModuleQuery(query);
  if (parsed.length < 2) {
    return false;
  }

  const modules = parsed
    .map(p => p.module)
    .filter(m => m !== 'unknown');

  const uniqueModules = new Set(modules);
  return uniqueModules.size >= 2;
}

/**
 * Get color for module badge
 */
export function getModuleColor(module) {
  switch (module) {
    case 'serotype':
      return '#FF6B6B'; // Red
    case 'nmr':
      return '#4ECDC4'; // Teal
    case 'carbuilder':
      return '#95E1D3'; // Green
    case 'species':
      return '#FFE66D'; // Yellow
    default:
      return '#CCCCCC'; // Gray
  }
}

/**
 * Get icon for module
 */
export function getModuleIcon(module) {
  switch (module) {
    case 'serotype':
      return 'tag';
    case 'nmr':
      return 'chart line';
    case 'carbuilder':
      return 'connectdevelop';
    case 'species':
      return 'dna';
    default:
      return 'question circle';
  }
}
